/**
 * AuditLog.tsx — Hash-chained audit log viewer (D-02, ARCH §7.9).
 *
 * Features:
 *   - GET /audit with from/to/actor/action/target filters + cursor pagination
 *   - Export button → GET /audit/export (NDJSON download)
 *   - Verify Chain → POST /audit/verify → shows verified=true/false + firstBrokenAt
 *
 * Security (T-04-34):
 *   - Verify Chain button surfaces verified=false + firstBrokenAt so tampering is visible
 *   - No Art. 9 content — metadata shown is IDs and action facts only (ARCH §7.9)
 *
 * Times render in Europe/Berlin (D-09).
 */

import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { auditApi, operatorsApi, ApiRequestError } from '../lib/api-client.js';
import type { AuditRowDto, VerifyChainResult } from '@crm/types';
import {
  Button,
  Input,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  useToast,
} from '@crm/ui';
import { formatBerlin } from '../lib/time.js';

// ---------------------------------------------------------------------------
// Verify chain result dialog
// ---------------------------------------------------------------------------

interface VerifyResultDialogProps {
  result: VerifyChainResult;
  onClose: () => void;
}

function VerifyResultDialog({ result, onClose }: VerifyResultDialogProps) {
  return (
    <Dialog
      open
      title={result.verified ? '✓ Chain verified' : '✗ Chain broken'}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          {result.verified ? (
            <Badge variant="success">Verified</Badge>
          ) : (
            <Badge variant="danger">Tampered / broken</Badge>
          )}
          <span className="text-sm text-text-muted">
            {result.rowsChecked} rows checked
          </span>
        </div>

        {!result.verified && result.firstBrokenAt && (
          <div className="bg-danger/10 border border-danger/30 rounded p-3">
            <p className="text-xs font-semibold text-danger mb-1">
              First broken link
            </p>
            <p className="text-sm font-mono text-text">
              {formatBerlin(result.firstBrokenAt)}
            </p>
            <p className="text-xs text-text-muted mt-1">
              Hash chain integrity was broken at this timestamp. All rows after
              this point may have been tampered with.
            </p>
          </div>
        )}

        {result.verified && (
          <p className="text-sm text-text-muted">
            The audit log hash chain is intact — no tampering detected.
          </p>
        )}

        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Metadata cell — compact JSON render
// ---------------------------------------------------------------------------

function MetadataCell({ metadata }: { metadata: Record<string, unknown> | null }) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return <span className="text-text-subtle">—</span>;
  }
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-text-muted hover:text-text">
        {Object.keys(metadata).length} field{Object.keys(metadata).length !== 1 ? 's' : ''}
      </summary>
      <pre className="mt-1 bg-bg-input rounded p-2 text-[10px] font-mono text-text overflow-auto max-w-xs max-h-24">
        {JSON.stringify(metadata, null, 2)}
      </pre>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Filters form
// ---------------------------------------------------------------------------

interface Filters {
  from: string;
  to: string;
  actor: string;
  action: string;
  target: string;
  limit: string;
}

const DEFAULT_FILTERS: Filters = {
  from: '',
  to: '',
  actor: '',
  action: '',
  target: '',
  limit: '50',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AuditLog() {
  const { addToast } = useToast();
  const [filters, setFilters] = React.useState<Filters>(DEFAULT_FILTERS);
  const [applied, setApplied] = React.useState<Filters>(DEFAULT_FILTERS);
  const [cursor, setCursor] = React.useState<string | undefined>();
  const [allRows, setAllRows] = React.useState<AuditRowDto[]>([]);
  const [verifyResult, setVerifyResult] = React.useState<VerifyChainResult | null>(null);

  // Fetch all operators (admins + chatters) for actor ID → displayName resolution (SC-6 CR-03).
  // chattersApi.list() only returns role='chatter' rows, so admin actors rendered as "Removed
  // operator". operatorsApi.list() (GET /users) returns ALL active operators incl. admins.
  const operatorsQ = useQuery({
    queryKey: ['operators'],
    queryFn: () => operatorsApi.list(),
    staleTime: 5 * 60_000,
  });
  // Build lookup map: operator id → displayName (or email fallback)
  const chatterById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const o of operatorsQ.data ?? []) {
      m.set(o.id, o.displayName || o.email);
    }
    return m;
  }, [operatorsQ.data]);

  const setField = (field: keyof Filters) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['audit', applied, cursor],
    queryFn: () =>
      auditApi.list({
        from: applied.from || undefined,
        to: applied.to || undefined,
        actor: applied.actor || undefined,
        action: applied.action || undefined,
        target: applied.target || undefined,
        cursor,
        limit: Number(applied.limit) || 50,
      }),
    staleTime: 10_000,
  });

  // Accumulate rows across pages
  React.useEffect(() => {
    if (data) {
      if (!cursor) {
        // Fresh search — replace
        setAllRows(data);
      } else {
        // Paginating — append
        setAllRows((prev) => [...prev, ...data]);
      }
    }
  }, [data, cursor]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setApplied(filters);
    setCursor(undefined);
    setAllRows([]);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setCursor(undefined);
    setAllRows([]);
  };

  const handleLoadMore = () => {
    const last = allRows[allRows.length - 1];
    if (last) setCursor(last.occurredAt);
  };

  // Export — navigate to the URL (triggers NDJSON download)
  const handleExport = () => {
    const url = auditApi.exportUrl({
      from: applied.from || undefined,
      to: applied.to || undefined,
    });
    window.open(url, '_blank', 'noopener');
  };

  // Verify chain
  const verifyMut = useMutation({
    mutationFn: () =>
      auditApi.verifyChain(
        applied.from || undefined,
        applied.to || undefined,
      ),
    onSuccess: (result) => {
      setVerifyResult(result);
    },
    onError: (err) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : 'Verify failed';
      addToast(msg, 'danger');
    },
  });

  const hasMore = data && data.length === (Number(applied.limit) || 50);

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text">Audit Log</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Hash-chained append-only log. No Art. 9 content — IDs and action facts only.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
          >
            Export NDJSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            loading={verifyMut.isPending}
            onClick={() => verifyMut.mutate()}
          >
            Verify Chain
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-3">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-3 gap-3 items-end"
          >
            <Input
              label="From (ISO date)"
              type="datetime-local"
              value={filters.from}
              onChange={setField('from')}
            />
            <Input
              label="To (ISO date)"
              type="datetime-local"
              value={filters.to}
              onChange={setField('to')}
            />
            <Input
              label="Limit"
              type="number"
              min="1"
              max="200"
              value={filters.limit}
              onChange={setField('limit')}
            />
            <Input
              label="Actor ID"
              value={filters.actor}
              onChange={setField('actor')}
              placeholder="chatter UUID"
            />
            <Input
              label="Action"
              value={filters.action}
              onChange={setField('action')}
              placeholder="e.g. auth.login"
            />
            <Input
              label="Target ID"
              value={filters.target}
              onChange={setField('target')}
            />
            <div className="col-span-3 flex gap-2 justify-end">
              <Button variant="outline" type="button" size="sm" onClick={handleReset}>
                Reset
              </Button>
              <Button type="submit" size="sm">
                Search
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {allRows.length} row{allRows.length !== 1 ? 's' : ''}
              {isFetching && (
                <span className="ml-2 text-text-subtle font-normal text-xs">
                  fetching…
                </span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && !allRows.length && (
            <div className="p-4 text-sm text-text-muted">Loading…</div>
          )}
          {!isLoading && allRows.length === 0 && (
            <div className="p-4 text-sm text-text-muted">No audit rows found.</div>
          )}
          {allRows.length > 0 && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time (Berlin)</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target type</TableHead>
                    <TableHead>Target ID</TableHead>
                    <TableHead>Metadata</TableHead>
                    <TableHead>Hash</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatBerlin(row.occurredAt)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {chatterById.has(row.actorId) ? (
                          <span title={row.actorId}>
                            {chatterById.get(row.actorId)}
                          </span>
                        ) : (
                          <span className="text-text-muted italic">
                            Removed operator
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.actorRole === 'admin' ? (
                          <Badge variant="info">admin</Badge>
                        ) : (
                          <Badge variant="outline">chatter</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.action}
                      </TableCell>
                      <TableCell className="text-xs">{row.targetType}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.targetId.slice(0, 8)}…
                      </TableCell>
                      <TableCell>
                        <MetadataCell metadata={row.metadata} />
                      </TableCell>
                      <TableCell className="font-mono text-[10px] text-text-subtle">
                        {row.rowHash.slice(0, 12)}…
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {hasMore && (
                <div className="flex justify-center p-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMore}
                    loading={isFetching}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {verifyResult && (
        <VerifyResultDialog
          result={verifyResult}
          onClose={() => setVerifyResult(null)}
        />
      )}
    </div>
  );
}
